import React, { useMemo } from "react";
import moment from "moment";
import { useNavigate } from "react-router-dom";
import { getBehaviourColourForPosition } from "@signolteam/shared/colours";
import { formatNumber } from "@signolteam/shared/units-of-measure";
import { useDCContext, useAuthContext } from "@src/context";
import { useManagerContext } from "@src/context/managerContext";
import ManagerHooks from "@src/context/hooks/managerHooks";
import { AppUtils, SignolUtils, StorageUtils } from "@src/utils";
import {
  CarouselSignol,
  Divider,
  Loader,
  Tooltip,
} from "@src/components/atoms";
import { CardContainer } from "@src/components/cards";
import {
  ManagerDashboardInfo,
  ManagersBehaviourChangeCard,
} from "@src/components/cards/manager";
import MonthlyDateDropDown from "@src/components/atoms/manager/MonthlyDateDropDown";
import QuicksightDashboard from "@src/components/atoms/manager/QuicksightDashboard";
import ManagerOverviewHeader from "./ManagerOverviewHeader";
import { BehiveLayout } from "@src/components/layout";
import { IMPERSONATED_SESSION_COOKIE } from "@src/constants/constants";
import BehiveTab from "../behaviours/BeehiveTab";

export const ManagerOverviewPage = () => {
  const { profile, selectedPeriod, selectedBehaviour, setSelectedBehaviour } =
    useManagerContext();
  const dc = useDCContext();
  const auth = useAuthContext();
  const navigate = useNavigate();

  const isMaritime = profile.customer.industry === "shipping";
  const customerLaunchDate = moment.utc(profile.customer.contractStartDate);
  const showQuicksightDashboard =
    isMaritime && !!profile.customer.quicksightDashboardId;

  //Hooks for data
  const {
    data: { numberOfVehicles } = {},
    isPending: isPendingVehicles,
    error: errorVehicles,
  } = ManagerHooks.useGetVehicles(auth.activeRole);
  const {
    data: {
      //countEventsPreSignol,
      countEventsPostSignol,
      //countOperators,
      countOperatorUsers,
    } = {},
    isPending: isPendingEventTotals,
    error: errorEventTotals,
  } = ManagerHooks.useGetEventTotals(auth.activeRole);
  const {
    data: behaviourSuccessVsPreSignol = [],
    isPending: isPendingBehaviourSuccessVsPre,
    error: errorBehaviourSuccessVsPre,
  } = ManagerHooks.useGetBehaviourSuccessVsPreSignol(
    auth.activeRole,
    null,
    selectedPeriod,
    AppUtils.endOfMonthIfValidDate(selectedPeriod),
  );
  const {
    data: { notificationTotals } = {},
    isPending: isPendingNotificationTotals,
    error: errorNotificationTotals,
  } = ManagerHooks.useGetNotificationTotals(auth.activeRole);

  //Events
  const handleBehaviourCardClick = (acronym) => {
    if (acronym !== selectedBehaviour.behaviourAcronym) {
      const newBehaviour = profile.behaviours.find(
        (b) => b.behaviourAcronym === acronym,
      );
      setSelectedBehaviour(newBehaviour);
    }
    navigate("/dashboard/manager/behaviours");
  };

  //DC
  const customerInsight = useMemo(
    () =>
      dc.existsDynamicReplacements(
        `fuel_manager__insights__${profile.customer.customerCode.toLowerCase()}`,
      )
        ? dc.getDynamicReplacement(
            `fuel_manager__insights__${profile.customer.customerCode.toLowerCase()}`,
          )
        : "",
    [dc, profile.customer.customerCode],
  );

  const behiveLayoutDCString = `layouts__${profile.customer.customerCode.toLowerCase()}__overview`;
  const isImpersonating =
    Object.keys(StorageUtils.getObjectItem(IMPERSONATED_SESSION_COOKIE) || {})
      .length > 0;
  const shouldShowNewDashboard =
    dc.existsDynamicReplacements(behiveLayoutDCString) &&
    (AppUtils.isFeatureAvailableForCustomer(
      "BehiveDashboards",
      profile.customer.customerCode,
    ) || // customer is allowed to see
      isImpersonating || // or admin is checking in prod
      (!SignolUtils.isProd() && !SignolUtils.isDemo())); // or it's not prod/demo so we want to test it

  const sectionBehaviourChange = (
    <section>
      <div className="flex flex-col-reverse xl:flex-row justify-between w-full mb-6 relative flex-wrap">
        <div className="w-full xl:w-1/3 mt-6 xl:mt-0 pl-12 flex justify-center xl:justify-start">
          <MonthlyDateDropDown />
          <div onClick={(e) => e.stopPropagation()} className="ml-3 mt-2">
            <Tooltip
              content={[
                dc.getDynamicReplacement(
                  "fuel_manager__tooltip__overview_behaviour_change_tooltip",
                  {
                    EVENT_NAME_PLURAL:
                      profile.customer.preferredEventNamePlural?.toLowerCase(),
                    EVENT_NAME:
                      profile.customer.preferredEventName?.toLowerCase(),
                  },
                ),
              ]}
              direction={"down-left"}
              absolute={false}
            />
          </div>
        </div>
        <div className="w-full xl:w-1/3 flex justify-center">
          <div className="font-extrabold text-2xl md:text-4xl m-0">
            {dc.getDynamicReplacement(
              "fuel_manager__behaviour_change__title",
              {},
              "Behaviour Change",
            )}
          </div>
        </div>
        <div className="absolute lg:static top-0 right-0 md:w-1/3 flex flex-row-reverse items-center"></div>
      </div>

      <div className="w-full h-72 mb-12 flex justify-center align-center">
        {isPendingBehaviourSuccessVsPre ? (
          <Loader size="2xl" />
        ) : errorBehaviourSuccessVsPre ? (
          <div className="text-red-500">Error fetching behaviour data...</div>
        ) : (
          <div className="w-full h-full p-4">
            {/* display the no data message if the post signol or change values are null temporary fix for virgin missing data to be removed when no data states for cards are added */}
            {behaviourSuccessVsPreSignol.length > 0 ? (
              <>
                <div
                  className={
                    behaviourSuccessVsPreSignol.length <= 4
                      ? "hidden xl:flex w-full items-stretch justify-center gap-4 min-h-0"
                      : "hidden"
                  }
                >
                  {behaviourSuccessVsPreSignol.map((bvr) => {
                    return (
                      <div className="h-full" key={bvr.behaviourAcronym}>
                        <ManagersBehaviourChangeCard
                          behaviourName={bvr.behaviourName}
                          behaviourAcronym={bvr.behaviourAcronym}
                          clickHandler={handleBehaviourCardClick}
                          behaviourColour={
                            getBehaviourColourForPosition(bvr.behaviourPosition)
                              ?.name
                          }
                          attributeText="Average success rate:"
                          postSignolValue={
                            bvr.avgSuccessRatePostSignolWeighted?.value
                          }
                          postSignolStandardError={
                            bvr.avgSuccessRatePostSignolWeighted
                              ?.standardError95
                          }
                          changeVsPreValue={bvr.avgSuccessRateChange?.value}
                          changeVsPreStandardError={
                            bvr.avgSuccessRateChange?.standardError95
                          }
                        />
                      </div>
                    );
                  })}
                </div>
                <div
                  className={
                    behaviourSuccessVsPreSignol.length <= 4 ? "xl:hidden" : ""
                  }
                >
                  <CarouselSignol infinite={true}>
                    {behaviourSuccessVsPreSignol.map((bvr) => {
                      return (
                        <div
                          className="h-full"
                          style={{ width: "255px" }}
                          key={bvr.behaviourAcronym}
                        >
                          <ManagersBehaviourChangeCard
                            behaviourName={bvr.behaviourName}
                            behaviourAcronym={bvr.behaviourAcronym}
                            clickHandler={handleBehaviourCardClick}
                            behaviourColour={
                              getBehaviourColourForPosition(
                                bvr.behaviourPosition,
                              ).name
                            }
                            attributeText="Average success rate:"
                            postSignolValue={
                              bvr.avgSuccessRatePostSignolWeighted?.value
                            }
                            postSignolStandardError={
                              bvr.avgSuccessRatePostSignolWeighted
                                ?.standardError95
                            }
                            changeVsPreValue={bvr.avgSuccessRateChange?.value}
                            changeVsPreStandardError={
                              bvr.avgSuccessRateChange?.standardError95
                            }
                          />
                        </div>
                      );
                    })}
                  </CarouselSignol>
                </div>
              </>
            ) : (
              <div className="w-full h-full flex justify-center items-center px-12">
                {`We currently don't have data for this month. If you'd like to discuss this further, please contact your Signol account manager.`}
              </div>
            )}
          </div>
        )}
      </div>
    </section>
  );

  const oldDashboard = (
    <>
      {/* Info Cards */}
      <section className="px-12 pt-6 flex gap-2">
        <ManagerDashboardInfo
          text={dc.getDynamicReplacement(
            "fuel_manager__overview__info_cards__launched_card",
          )}
          value={customerLaunchDate.format("Do MMM YYYY")}
          isLoading={false}
          className="mr-3"
        />

        {((isMaritime && errorVehicles) ||
          (!isMaritime && errorEventTotals)) && (
          <ManagerDashboardInfo
            text={dc.getDynamicReplacement(
              "fuel_manager__overview__info_cards__assets_card",
            )}
            value={isMaritime ? numberOfVehicles : countOperatorUsers}
            isLoading={isMaritime ? isPendingVehicles : isPendingEventTotals}
            className="mr-3"
          />
        )}
      </section>
      <Divider padding={true} />

      {showQuicksightDashboard && <QuicksightDashboard hideErrors />}

      {sectionBehaviourChange}
      <Divider padding={true} extraTopPadding={true} />

      {!!customerInsight && (
        <>
          <section className="px-12 py-6 flex flex-col items-center">
            <div className="font-extrabold text-2xl md:text-4xl m-0 mb-6">
              Latest Insight
            </div>
            <div className="lg:w-2/3">
              <CardContainer padding={6} shadow={false}>
                <div className="font-bold md:font-extrabold text-base md:text-lg text-neutral-500 px-8">
                  {customerInsight}
                </div>
              </CardContainer>
            </div>
          </section>
          <Divider />
        </>
      )}

      {/* Overview Activity */}
      <section className="px-12 pt-6 mb-12 flex flex-col items-center">
        <div className="flex flex-col-reverse lg:flex-row justify-between w-full mb-6 md:mb-20 relative flex-wrap">
          <div className="w-full lg:w-1/3 mt-6 lg:mt-0 flex justify-center md:justify-start"></div>
          <div className="w-full lg:w-1/3 flex justify-center">
            <div className="font-extrabold text-2xl md:text-4xl m-0">
              Activity
            </div>
          </div>
          <div className="absolute lg:static top-0 right-0 md:w-1/3 flex flex-row-reverse items-center"></div>
        </div>
        <div className="flex w-full lg:px-40 justify-center flex-wrap gap-3">
          <ManagerDashboardInfo
            highlight={true}
            text={dc.getDynamicReplacement(
              "fuel_manager__overview__info_cards__analysed_events_card",
            )}
            value={formatNumber(countEventsPostSignol).asString}
            isLoading={isPendingEventTotals}
            className="mb-3 mr-6"
          />
          {errorNotificationTotals ? (
            <div className="text-red-500">
              Error fetching notification data...
            </div>
          ) : (
            <ManagerDashboardInfo
              highlight={true}
              text={dc.getDynamicReplacement(
                "fuel_manager__overview__info_cards__reports_delivered_card",
              )}
              value={
                formatNumber(notificationTotals?.countDelivered ?? 0).asString
              }
              isLoading={isPendingNotificationTotals}
              className="mb-3"
            />
          )}
        </div>
      </section>
    </>
  );

  return (
    <div>
      {shouldShowNewDashboard ? (
        <>
          <ManagerOverviewHeader newHeader={true} />
          <BehiveTab
            behiveLayoutDCString={behiveLayoutDCString}
            behaviourCardClickThroughHandler={handleBehaviourCardClick}
          />
        </>
      ) : (
        <>
          <ManagerOverviewHeader />
          {oldDashboard}
        </>
      )}
    </div>
  );
};
